import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Zap, Crown, Users } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    priceId: null,
    description: 'Perfect for occasional events',
    features: [
      '3 events per month',
      'Up to 10 guests per event',
      'Basic voting features',
      'Email invitations',
      'Real-time results',
      'Basic event templates',
    ],
    icon: Check,
    popular: false,
  },
  {
    id: 'family',
    name: 'Family',
    price: '$3.99',
    priceId: 'price_1RsSRcAX6lBz9Nrzs9e5NvEJ',
    description: 'Perfect for families organizing together',
    features: [
      '10 events per month',
      'Up to 50 guests per event',
      'Unlimited family members',
      'Family dashboard & templates',
      'Recurring events',
      'Basic custom RSVP (3 questions)',
      'Calendar integration',
    ],
    icon: Users,
    popular: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$15.99',
    priceId: 'price_1RrwqFAX6lBz9Nrz5WrpqptA',
    description: 'For event planners and small businesses',
    features: [
      'Unlimited events & guests',
      'Client collaboration portal',
      'Professional templates',
      'Unlimited custom RSVP',
      'Basic white-label branding',
      'Standard analytics & CSV export',
      'Priority support',
    ],
    icon: Crown,
    popular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$49.99',
    priceId: 'price_1RrwvxAX6lBz9Nrz6bCrwoFz',
    description: 'Advanced features for large organizations',
    features: [
      'Everything in Professional',
      'Advanced white-label & custom domain',
      'Advanced analytics & PDF reports',
      'Smart voting & host intelligence',
      'API access & webhooks',
      'Dedicated account manager',
      'Custom training & onboarding',
    ],
    icon: Zap,
    popular: false,
  },
];

export const SubscriptionPlans = () => {
  const { subscribed, subscription_tier, createCheckout, openCustomerPortal } = useSubscription();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handlePlanSelect = async (plan: typeof plans[0]) => {
    if (!plan.priceId) return;
    
    setLoadingPlan(plan.id);
    await createCheckout(plan.priceId);
    setLoadingPlan(null);
  };

  const isCurrentPlan = (planId: string) => {
    if (planId === 'free' && (!subscribed || subscription_tier === 'free')) return true;
    return subscribed && subscription_tier === planId;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-foreground mb-4">Choose Your Plan</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Upgrade to unlock powerful features and create unlimited events with AI-powered invitations.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const isCurrentPlanActive = isCurrentPlan(plan.id);
          
          return (
            <Card 
              key={plan.id} 
              className={`relative ${plan.popular ? 'border-primary shadow-lg scale-105' : ''} ${isCurrentPlanActive ? 'ring-2 ring-primary' : ''}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                  Most Popular
                </Badge>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4">
                  <div className={`p-3 rounded-full ${plan.popular ? 'bg-primary/10' : 'bg-secondary'}`}>
                    <Icon className={`h-6 w-6 ${plan.popular ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="text-3xl font-bold text-foreground">
                  {plan.price}
                  {plan.price !== '$0' && <span className="text-sm text-muted-foreground">/month</span>}
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                {isCurrentPlanActive ? (
                  <div className="w-full">
                    <Button variant="outline" className="w-full mb-2" disabled>
                      Current Plan
                    </Button>
                    {subscribed && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={openCustomerPortal}
                      >
                        Manage Subscription
                      </Button>
                    )}
                  </div>
                ) : plan.priceId ? (
                  <Button
                    className="w-full"
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => handlePlanSelect(plan)}
                    disabled={loadingPlan === plan.id}
                  >
                    {loadingPlan === plan.id ? 'Loading...' : `Upgrade to ${plan.name}`}
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full" disabled>
                    Current Plan
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      <div className="text-center mt-8">
        <p className="text-sm text-muted-foreground">
          All plans include a 14-day free trial. Cancel anytime.
        </p>
      </div>
    </div>
  );
};